export type LayoutInk =
  | 'black' | 'white' | 'red' | 'dark' | 'grey'
  | 'blue' | 'cyan' | 'green' | 'orange' | 'purple';

export type LvglRenderWidget = {
  id?: string;
  type: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  text?: string;
  align?: 'left' | 'center' | 'right';
  color?: string;
  background?: string;
  border?: boolean;
  emit?: string;
  children?: LvglRenderWidget[];
};

/** Named inks — EPD trio plus LVGL-demo palette (Material-ish, like lv_palette_main). */
const INK: Record<string, [number, number, number]> = {
  black: [17, 17, 17],
  white: [255, 255, 255],
  red: [244, 67, 54],
  dark: [33, 33, 33],
  grey: [224, 224, 224],
  blue: [33, 150, 243],
  cyan: [0, 188, 212],
  green: [76, 175, 80],
  orange: [255, 152, 0],
  purple: [156, 39, 176],
};

/** LVGL default light theme screen. */
const SCREEN_BG: [number, number, number, number] = [238, 242, 248, 255];

/** Button radius ≈ lv_dpx(8). */
const BTN_RADIUS = 12;
const BTN_BORDER = [33, 150, 243] as const;
const FONT_PX = 14;

const PAINTABLE = new Set(['container', 'label', 'icon', 'button', 'switch', 'gauge', 'progress']);

export function layoutInkRgb(name?: string, fallback: LayoutInk = 'black'): [number, number, number] {
  const key = String(name || fallback).toLowerCase();
  return INK[key] || INK[fallback];
}

export function valuePercent(text?: string): number {
  const n = Number(String(text ?? '').replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.max(0, Math.min(100, n));
}

function setPixel(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  x: number,
  y: number,
  r: number,
  g: number,
  b: number,
  a = 255,
): void {
  if (x < 0 || y < 0 || x >= w || y >= h) {
    return;
  }
  const i = (y * w + x) * 4;
  if (a >= 255) {
    data[i] = r;
    data[i + 1] = g;
    data[i + 2] = b;
    data[i + 3] = 255;
    return;
  }
  const inv = 1 - a / 255;
  data[i] = Math.round(r * (a / 255) + data[i] * inv);
  data[i + 1] = Math.round(g * (a / 255) + data[i + 1] * inv);
  data[i + 2] = Math.round(b * (a / 255) + data[i + 2] * inv);
  data[i + 3] = 255;
}

function fillRect(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  x0: number,
  y0: number,
  rw: number,
  rh: number,
  r: number,
  g: number,
  b: number,
): void {
  const x1 = Math.min(w, Math.max(0, Math.floor(x0)));
  const y1 = Math.min(h, Math.max(0, Math.floor(y0)));
  const x2 = Math.min(w, Math.max(0, Math.floor(x0 + rw)));
  const y2 = Math.min(h, Math.max(0, Math.floor(y0 + rh)));
  for (let y = y1; y < y2; y++) {
    for (let x = x1; x < x2; x++) {
      setPixel(data, w, h, x, y, r, g, b);
    }
  }
}

function fillRoundRect(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  x0: number,
  y0: number,
  rw: number,
  rh: number,
  radius: number,
  r: number,
  g: number,
  b: number,
): void {
  const rad = Math.max(0, Math.min(radius, Math.floor(Math.min(rw, rh) / 2)));
  for (let y = 0; y < rh; y++) {
    for (let x = 0; x < rw; x++) {
      let inside = true;
      if (x < rad && y < rad) {
        inside = (rad - x) ** 2 + (rad - y) ** 2 <= rad * rad;
      } else if (x >= rw - rad && y < rad) {
        inside = (x - (rw - rad - 1)) ** 2 + (rad - y) ** 2 <= rad * rad;
      } else if (x < rad && y >= rh - rad) {
        inside = (rad - x) ** 2 + (y - (rh - rad - 1)) ** 2 <= rad * rad;
      } else if (x >= rw - rad && y >= rh - rad) {
        inside = (x - (rw - rad - 1)) ** 2 + (y - (rh - rad - 1)) ** 2 <= rad * rad;
      }
      if (inside) {
        setPixel(data, w, h, Math.floor(x0 + x), Math.floor(y0 + y), r, g, b);
      }
    }
  }
}

function strokeRoundRect(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  x0: number,
  y0: number,
  rw: number,
  rh: number,
  radius: number,
  r: number,
  g: number,
  b: number,
): void {
  const rad = Math.max(0, Math.min(radius, Math.floor(Math.min(rw, rh) / 2)));
  for (let y = 0; y < rh; y++) {
    for (let x = 0; x < rw; x++) {
      let onEdge = false;
      if (x < rad && y < rad) {
        const d2 = (rad - x) ** 2 + (rad - y) ** 2;
        onEdge = d2 <= rad * rad && d2 >= (rad - 1.5) ** 2;
      } else if (x >= rw - rad && y < rad) {
        const d2 = (x - (rw - rad - 1)) ** 2 + (rad - y) ** 2;
        onEdge = d2 <= rad * rad && d2 >= (rad - 1.5) ** 2;
      } else if (x < rad && y >= rh - rad) {
        const d2 = (rad - x) ** 2 + (y - (rh - rad - 1)) ** 2;
        onEdge = d2 <= rad * rad && d2 >= (rad - 1.5) ** 2;
      } else if (x >= rw - rad && y >= rh - rad) {
        const d2 = (x - (rw - rad - 1)) ** 2 + (y - (rh - rad - 1)) ** 2;
        onEdge = d2 <= rad * rad && d2 >= (rad - 1.5) ** 2;
      } else {
        onEdge = x === 0 || y === 0 || x === rw - 1 || y === rh - 1;
      }
      if (onEdge) {
        setPixel(data, w, h, Math.floor(x0 + x), Math.floor(y0 + y), r, g, b);
      }
    }
  }
}

function fillCircle(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  cx: number,
  cy: number,
  radius: number,
  r: number,
  g: number,
  b: number,
): void {
  const rad = Math.max(1, Math.floor(radius));
  for (let y = -rad; y <= rad; y++) {
    for (let x = -rad; x <= rad; x++) {
      if (x * x + y * y <= rad * rad) {
        setPixel(data, w, h, Math.floor(cx + x), Math.floor(cy + y), r, g, b);
      }
    }
  }
}

function strokeCircle(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  cx: number,
  cy: number,
  radius: number,
  r: number,
  g: number,
  b: number,
): void {
  const rad = Math.max(1, Math.floor(radius));
  const outer = rad * rad;
  const inner = (rad - 2) * (rad - 2);
  for (let y = -rad; y <= rad; y++) {
    for (let x = -rad; x <= rad; x++) {
      const d2 = x * x + y * y;
      if (d2 <= outer && d2 >= inner) {
        setPixel(data, w, h, Math.floor(cx + x), Math.floor(cy + y), r, g, b);
      }
    }
  }
}

function widgetCaption(widget: LvglRenderWidget): string {
  const text = String(widget.text || '').trim();
  if (text) {
    return text;
  }
  const id = String(widget.id || '').trim();
  if (id) {
    return id;
  }
  if (widget.type === 'button') {
    return 'Button';
  }
  if (widget.type === 'icon') {
    return '◆';
  }
  if (widget.type === 'gauge' || widget.type === 'progress' || widget.type === 'switch') {
    return '0';
  }
  return 'Label';
}

type TextCtx = CanvasRenderingContext2D;

function drawCaption(
  textCtx: TextCtx | null,
  caption: string,
  x: number,
  y: number,
  rw: number,
  rh: number,
  fg: [number, number, number],
  align?: string,
  fontPx = FONT_PX,
): void {
  if (!textCtx) {
    return;
  }
  textCtx.font = `${fontPx}px Montserrat, "DejaVu Sans", Arial, sans-serif`;
  textCtx.fillStyle = `rgb(${fg[0]},${fg[1]},${fg[2]})`;
  textCtx.textAlign = align === 'center' ? 'center' : align === 'right' ? 'right' : 'left';
  textCtx.textBaseline = 'middle';
  const pad = 8;
  let tx = x + pad;
  if (align === 'center') {
    tx = x + rw / 2;
  } else if (align === 'right') {
    tx = x + rw - pad;
  }
  textCtx.save();
  textCtx.beginPath();
  textCtx.rect(x, y, rw, rh);
  textCtx.clip();
  textCtx.fillText(caption, tx, y + rh / 2);
  textCtx.restore();
}

function paintOne(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  widget: LvglRenderWidget,
  ox: number,
  oy: number,
  textCtx: TextCtx | null,
  selectedId?: string,
): void {
  if (!PAINTABLE.has(widget.type)) {
    return;
  }
  const x = Math.floor(ox + (Number(widget.x) || 0));
  const y = Math.floor(oy + (Number(widget.y) || 0));
  const rw = Math.max(8, Math.floor(Number(widget.width) || 80));
  const rh = Math.max(8, Math.floor(Number(widget.height) || 40));
  const caption = widgetCaption(widget);
  const fg = layoutInkRgb(widget.color, 'black');
  const nested = Boolean(widget.children && widget.children.length);

  if (widget.type === 'container') {
    const bg = layoutInkRgb(widget.background, 'white');
    fillRect(data, width, height, x, y, rw, rh, bg[0], bg[1], bg[2]);
    if (widget.border !== false) {
      strokeRoundRect(data, width, height, x, y, rw, rh, 0, 17, 17, 17);
    }
  } else if (widget.type === 'button') {
    const bg = layoutInkRgb(widget.background, 'white');
    fillRoundRect(data, width, height, x, y, rw, rh, BTN_RADIUS, bg[0], bg[1], bg[2]);
    strokeRoundRect(
      data, width, height, x, y, rw, rh, BTN_RADIUS,
      BTN_BORDER[0], BTN_BORDER[1], BTN_BORDER[2],
    );
    if (!nested) {
      drawCaption(textCtx, caption, x, y, rw, rh, fg, widget.align || 'center');
    }
  } else if (widget.type === 'progress') {
    const track = layoutInkRgb(widget.background, 'white');
    const bar = layoutInkRgb(widget.color, 'black');
    fillRect(data, width, height, x, y, rw, rh, track[0], track[1], track[2]);
    strokeRoundRect(data, width, height, x, y, rw, rh, 2, BTN_BORDER[0], BTN_BORDER[1], BTN_BORDER[2]);
    const fillW = Math.floor((rw * valuePercent(widget.text)) / 100);
    if (fillW > 0) {
      fillRect(data, width, height, x, y, fillW, rh, bar[0], bar[1], bar[2]);
    }
  } else if (widget.type === 'switch') {
    const on = valuePercent(widget.text) > 0
      || /^(on|true|1)$/i.test(String(widget.text || '').trim());
    const track = on
      ? layoutInkRgb(widget.color, 'blue')
      : layoutInkRgb(widget.background, 'grey');
    const radius = Math.floor(Math.min(rw, rh) / 2);
    fillRoundRect(data, width, height, x, y, rw, rh, radius, track[0], track[1], track[2]);
    const knobR = Math.max(4, Math.floor(rh / 2) - 3);
    const knobX = on
      ? x + rw - knobR - 3
      : x + knobR + 3;
    const knobY = y + Math.floor(rh / 2);
    fillCircle(data, width, height, knobX, knobY, knobR, 255, 255, 255);
  } else if (widget.type === 'gauge') {
    const face = layoutInkRgb(widget.background, 'white');
    const needle = layoutInkRgb(widget.color, 'blue');
    const cx = x + rw / 2;
    const cy = y + rh / 2;
    const rad = Math.floor(Math.min(rw, rh) / 2) - 2;
    fillCircle(data, width, height, cx, cy, rad, face[0], face[1], face[2]);
    // Demo-style coloured arcs (green / orange / red), like lv_demo_widgets meter
    strokeCircle(data, width, height, cx, cy, rad - 2, 76, 175, 80);
    strokeCircle(data, width, height, cx, cy, rad - 6, 255, 152, 0);
    strokeCircle(data, width, height, cx, cy, rad - 10, 244, 67, 54);
    strokeCircle(data, width, height, cx, cy, rad, 189, 189, 189);
    const pct = valuePercent(widget.text);
    const angle = (-Math.PI * 0.75) + (pct / 100) * Math.PI * 1.5;
    const nx = Math.cos(angle) * (rad - 14);
    const ny = Math.sin(angle) * (rad - 14);
    for (let t = 0; t <= 12; t++) {
      fillCircle(data, width, height, cx + (nx * t) / 12, cy + (ny * t) / 12, 2, needle[0], needle[1], needle[2]);
    }
    fillCircle(data, width, height, cx, cy, Math.max(6, Math.floor(rad * 0.12)), needle[0], needle[1], needle[2]);
    fillCircle(data, width, height, cx, cy, Math.max(10, Math.floor(rad * 0.38)), 255, 255, 255);
    drawCaption(textCtx, caption, x, y, rw, rh, needle, 'center', Math.max(12, Math.floor(rad * 0.32)));
  } else if (widget.type === 'icon') {
    const iconPx = Math.max(16, Math.floor(Math.min(rw, rh) * 0.55));
    drawCaption(textCtx, caption || '◆', x, y, rw, rh, fg, 'center', iconPx);
  } else {
    // label
    drawCaption(textCtx, caption, x, y, rw, rh, fg, widget.align || 'left');
  }

  if (optsSelected(widget, selectedId)) {
    strokeRoundRect(data, width, height, x - 1, y - 1, rw + 2, rh + 2, BTN_RADIUS + 1, 33, 150, 243);
  }

  if (widget.children?.length) {
    for (const child of widget.children) {
      paintOne(data, width, height, child, x, y, textCtx, selectedId);
    }
  }
}

function optsSelected(widget: LvglRenderWidget, selectedId?: string): boolean {
  return Boolean(selectedId && widget.id && selectedId === widget.id);
}

/**
 * Paint layout widgets into an RGBA buffer using LVGL-like geometry
 * (Montserrat-sized text, rounded buttons). Supports container hierarchy.
 */
export function paintLvglWidgets(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  widgets: LvglRenderWidget[],
  opts?: { selectedId?: string },
): void {
  const [sr, sg, sb, sa] = SCREEN_BG;
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    data[o] = sr;
    data[o + 1] = sg;
    data[o + 2] = sb;
    data[o + 3] = sa;
  }

  const textCanvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  const textCtx = textCanvas?.getContext('2d') || null;
  if (textCanvas && textCtx) {
    textCanvas.width = width;
    textCanvas.height = height;
    textCtx.clearRect(0, 0, width, height);
    textCtx.font = `${FONT_PX}px Montserrat, "DejaVu Sans", Arial, sans-serif`;
    textCtx.textBaseline = 'middle';
  }

  for (const widget of widgets) {
    paintOne(data, width, height, widget, 0, 0, textCtx, opts?.selectedId);
  }

  if (textCtx && textCanvas) {
    const overlay = textCtx.getImageData(0, 0, width, height).data;
    for (let i = 0; i < overlay.length; i += 4) {
      const a = overlay[i + 3];
      if (a === 0) {
        continue;
      }
      const inv = 1 - a / 255;
      data[i] = Math.round(overlay[i] * (a / 255) + data[i] * inv);
      data[i + 1] = Math.round(overlay[i + 1] * (a / 255) + data[i + 1] * inv);
      data[i + 2] = Math.round(overlay[i + 2] * (a / 255) + data[i + 2] * inv);
      data[i + 3] = 255;
    }
  }
}

export function hitTestLvglWidget(
  widgets: LvglRenderWidget[],
  x: number,
  y: number,
  pathPrefix: number[] = [],
): { widget: LvglRenderWidget; path: number[]; index: number } | null {
  for (let i = widgets.length - 1; i >= 0; i--) {
    const widget = widgets[i];
    if (!PAINTABLE.has(widget.type)) {
      continue;
    }
    const wx = Number(widget.x) || 0;
    const wy = Number(widget.y) || 0;
    const ww = Number(widget.width) || 80;
    const wh = Number(widget.height) || 40;
    if (x < wx || y < wy || x >= wx + ww || y >= wy + wh) {
      continue;
    }
    const path = [...pathPrefix, i];
    if (widget.children?.length) {
      const childHit = hitTestLvglWidget(widget.children, x - wx, y - wy, path);
      if (childHit) {
        return childHit;
      }
    }
    return { widget, path, index: i };
  }
  return null;
}

export { SCREEN_BG, FONT_PX };
